// TEMP (NEB-98752): client-side poll fallback for when the WS completion push finds no live connection. Remove once WS delivery is reliable.
import { logError } from '@ptek/client-logger';

interface ScribeJobPollFallbackDeps {
  getJobStatus: (id: string) => Promise<string | null>;
  onCompleted: () => Promise<void> | void;
  onFailed: () => void;
  /** Invoked exactly once when the cap is reached without a terminal status. */
  onExpired?: () => void;
  intervalMs?: number;
  maxMs?: number;
}

const TERMINAL_FAILED = ['FAILED', 'TIMED_OUT', 'CANCELLED'];

/**
 * Fire-and-forget poll loop that resolves a background scribe job's spinner by
 * polling job status, independent of the WS completion push. Schedules itself
 * with setTimeout (testable with fake timers) and never throws out of the loop.
 * On reaching the cap it stops polling and invokes `onExpired` (when provided)
 * so the caller can degrade any state that was waiting exclusively on this
 * poll; the still-pending job itself is left for the next reopen to recover.
 */
export function startScribeJobPollFallback(
  durableId: string,
  deps: ScribeJobPollFallbackDeps
): () => void {
  const { getJobStatus, onCompleted, onFailed, onExpired } = deps;
  const intervalMs = deps.intervalMs ?? 5000;
  const maxMs = deps.maxMs ?? 120000;
  const startedAt = Date.now();
  let cancelled = false;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const cancel = () => {
    cancelled = true;
    if (timer !== undefined) {
      clearTimeout(timer);
      timer = undefined;
    }
  };

  const poll = async (): Promise<void> => {
    if (cancelled) return;
    try {
      const status = await getJobStatus(durableId);
      // Appointment changes can cancel while the request is in flight. Do not
      // let that stale response invoke a terminal callback or schedule a tick.
      if (cancelled) return;
      if (status === 'COMPLETED') {
        await onCompleted();
        return;
      }
      if (status && TERMINAL_FAILED.includes(status)) {
        onFailed();
        return;
      }
    } catch (error) {
      if (cancelled) return;
      logError('scribeJobPollFallback', 'poll error:', error);
    }
    if (cancelled) return;
    // Not terminal (PENDING/RUNNING/null/error): keep polling until the cap.
    if (Date.now() - startedAt + intervalMs <= maxMs) {
      timer = setTimeout(() => {
        timer = undefined;
        void poll();
      }, intervalMs);
      return;
    }
    // On expiry, stop polling and let the next reopen retry while releasing poll-dependent state.
    onExpired?.();
  };

  timer = setTimeout(() => {
    timer = undefined;
    void poll();
  }, intervalMs);

  return cancel;
}
